import { Injectable } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable } from 'rxjs';
import { IWorkflow } from './models/workflow.model';
import { captionStringsForSDKMessages } from './capture-strings';
import { TranslateService } from '@ngx-translate/core';
import { describeError } from '@app/pages/capture/capture-files/capture-utils';

@Injectable({
  providedIn: 'root'
})
export class CaptureService {

  private logger: Logger;
  public workflow: IWorkflow;
  public workflowList: Array<IWorkflow>;
  public imagesArray: Array<any>;


  constructor(
    private loggingService: LoggingService,
    private translateService: TranslateService,
  ) {
    this.logger = this.loggingService.getLogger("[CaptureService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
   }

// #############################################################################################################################################################################
// #############################################################################################################################################################################
// ### 		SDK_VERSION            ###############################################################################################################################################
// #############################################################################################################################################################################
// #############################################################################################################################################################################

	/**
	 * Resolves the Kofax Mobile SDK build version, plus the Utility, Image Processor and
	 * Classifier library versions.
	 *
	 * Not the same as `kfxCordova.getkfxPluginVersion()`, which returns the Cordova plugin
	 * wrapper version declared in plugin.xml — Kofax does not bump that between SDK drops
	 * (3.8.0.2.126-182 and 3.8.0.4 both report "3.8"), so it cannot identify the installed
	 * build. See docs/kofax-sdk-upgrade.md.
	 *
	 * Diagnostic only: never blocks the capture chain — failures resolve with undefined.
	 */
	public getSDKVersions(): Observable<unknown> {
		const methodName = 'getSDKVersions() ';
		this.logger.info(methodName);
		const logger = this.logger;
		return new Observable((observer) => {
			try {
				const license = window['kfxCordova'].kfxUtilities.createLicense();
				license.getSDKVersions(
					function (sdkVersions) {
						observer.next(sdkVersions);
						observer.complete();
					},
					function (sdkVersionsError) {
						logger.error(methodName + 'ERROR: ' + describeError(sdkVersionsError));
						observer.next(undefined);
						observer.complete();
					}
				);
			} catch (error) {
				logger.error(methodName + 'ERROR reading SDK versions: ' + describeError(error));
				observer.next(undefined);
				observer.complete();
			}
		});
	}

// #############################################################################################################################################################################
// #############################################################################################################################################################################
// ### 		SET_LICENSE            ###############################################################################################################################################
// #############################################################################################################################################################################
// #############################################################################################################################################################################

  public setLicense(): Observable<void> {
		this.logger.info('setLicense()');
		const logger = this.logger;
		return new Observable((observer) => {
			try {
				const license = window['kfxCordova'].kfxUtilities.createLicense();
				license.setMobileSDKLicense(
					function (setLicenseSuccess) {
						const Logging = window['kfxCordova'].kfxUtilities.createLogging();
						Logging.enableMobileSDKLogging(function(enableLoggingSuccess) {
							console.log('JSON.stringify(enableLoggingSuccess)', JSON.stringify(enableLoggingSuccess));
							observer.next(setLicenseSuccess);
							observer.complete();
						},
						function(enableLoggingError){
							// SDK logging is non-essential — surface the failure but let the chain continue
							// rather than hang forever (previously this branch never completed the observable).
							logger.error('setLicense() - enableMobileSDKLogging error: ' + describeError(enableLoggingError));
							alert('Kofax logging error: ' + describeError(enableLoggingError));
							observer.next(setLicenseSuccess);
							observer.complete();
						});
					},
					function (setLicenseError) {
						// Surface the failure and unblock the chain instead of hanging forever.
						logger.error('setLicense() - setMobileSDKLicense error: ' + describeError(setLicenseError));
						alert('Kofax license error: ' + describeError(setLicenseError));
						observer.next();
						observer.complete();
					},
					"ttz,L@xtN#VD$B,#vF8vt@&4BXX$ljRnPf[089@p5n[6qb04[(NPIUEAWGUNKl;dfvkhzdf7rglcvjck=,mIOF&^BUL?!!!!!!0t"
				);
			} catch (error) {
				// A synchronous throw from the Kofax SDK bridge (e.g. native lib mismatch on a
				// given device/build) would otherwise break the chain silently with no app-level log.
				logger.error('setLicense() - exception creating/setting Kofax license: ' + describeError(error));
				alert('Kofax license error: ' + describeError(error));
				observer.next();
				observer.complete();
			}
		});
	}

// #############################################################################################################################################################################
// #############################################################################################################################################################################
// ### 		TRANSLATE_MESSAGES            ########################################################################################################################################
// #############################################################################################################################################################################
// #############################################################################################################################################################################

  public translateMessagesForSDK(): Observable<any> {
		this.logger.info('translateMessagesForSDK()');
    const translatedSDKMessages = {};
		return new Observable((observer) => {
			const messageCount = captionStringsForSDKMessages.length;
			captionStringsForSDKMessages.forEach((messageString, index) => {
				this.translateService.get(messageString).subscribe( async (translatedMessage: string) => {
					this.logger.info('translateMessagesForSDK() - translatedMessage: ' + JSON.stringify(translatedMessage));
					const dataToAssign = {[captionStringsForSDKMessages[index]]: translatedMessage};
					Object.assign(translatedSDKMessages, dataToAssign);
					if (messageCount - 1 === index) {
						observer.next(translatedSDKMessages);
						observer.complete();
					}
				});
			});
		});
	}

  // #############################################################################################################################################################################
	// #############################################################################################################################################################################
	// ### 	     SET_DEFAULT_CAPTURE_EXPERIENCE_OPTIONS           ##################################################################################################################
	// #############################################################################################################################################################################
	// #############################################################################################################################################################################

	public setDefaultCaptureExperienceOptions(translatedSDKMessages): Observable<any> {
		this.logger.info('setDefaultCaptureExperienceOptions()');
    const documentCaptureExperience = window['kfxCordova'].kfxUicontrols.createDocumentCaptureExperience();
    const documentCaptureExperienceOptions = documentCaptureExperience.getDocumentCaptureOptions();
    return new Observable((observer) => {
      documentCaptureExperienceOptions.CapturedMessage.message = translatedSDKMessages['CapturedMessage'];
      documentCaptureExperienceOptions.CenterMessage.message = translatedSDKMessages['CenterMessage'];
      documentCaptureExperienceOptions.HoldParallelMessage.message = translatedSDKMessages['HoldParallelMessage'];
      documentCaptureExperienceOptions.HoldSteadyMessage.message = translatedSDKMessages['HoldSteadyMessage'];
      documentCaptureExperienceOptions.RotateMessage.message = translatedSDKMessages['RotateMessage'];
      documentCaptureExperienceOptions.TiltForwardMessage.message = translatedSDKMessages['TiltForwardMessage'];
      documentCaptureExperienceOptions.TiltUpMessage.message = translatedSDKMessages['TiltUpMessage'];
      documentCaptureExperienceOptions.TutorialDismissMessage.message = translatedSDKMessages['TutorialDismissMessage'];
      documentCaptureExperienceOptions.UserInstructionMessage.message = translatedSDKMessages['UserInstructionMessage'];
      documentCaptureExperienceOptions.ZoomInMessage.message = translatedSDKMessages['ZoomInMessage'];
      documentCaptureExperienceOptions.ZoomOutMessage.message = translatedSDKMessages['ZoomOutMessage'];
      documentCaptureExperienceOptions.LookAndFeel.enableAnimationTutor = true;
      documentCaptureExperienceOptions.CaptureCriteria.DocumentDetectionSettings.DetectionSettings.minFillFraction = 0.65;
      documentCaptureExperienceOptions.CaptureCriteria.DocumentDetectionSettings.DetectionSettings.maxFillFraction = 1.1;
      documentCaptureExperienceOptions.CaptureCriteria.glareDetectionEnabled = false;
      documentCaptureExperienceOptions.CaptureCriteria.launchGlareRemoverExperience = false;
      observer.next(documentCaptureExperienceOptions);
      observer.complete();
    });
	}
}